import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import { getContent, getContentByGenre, getMostPopularContent, getBestRatedContent, getLatestContent, getHiddenGemsContent, type ContentItem } from '../services/contentService';
import { getContentLikes, getUserLike, toggleLike } from '../services/likesService';
import { shareContent } from '../services/shareService';

export type ContentCategory = 'movie' | 'series' | 'short' | 'book' | 'comic' | 'novel' | 'anime';

interface ContentState {
  content: Record<ContentCategory, ContentItem[]>;
  loading: Record<ContentCategory, boolean>;
  error: Record<ContentCategory, string | null>;
  selectedGenre: Record<ContentCategory, string>;
  userLikes: Record<string, 'like' | 'dislike' | null>;
  likeCounts: Record<string, { likes: number; dislikes: number }>;
  liking: Set<string>;
  // Home page content types
  popularContent: Record<ContentCategory, ContentItem[]>;
  bestRatedContent: Record<ContentCategory, ContentItem[]>;
  latestContent: Record<ContentCategory, ContentItem[]>;
  hiddenGemsContent: Record<ContentCategory, ContentItem[]>;
  homeContentLoading: Record<ContentCategory, boolean>;
}

interface ContentActions {
  loadContent: (category: ContentCategory, userId?: string) => Promise<void>;
  setSelectedGenre: (category: ContentCategory, genre: string) => void;
  handleLike: (contentId: string, category: ContentCategory, userId: string) => Promise<void>;
  handleShare: (contentId: string, category: ContentCategory, userId?: string, shareMethod?: 'link' | 'social' | 'embed', platform?: string) => Promise<void>;
  clearContent: (category: ContentCategory) => void;
  // Home page content loading
  loadPopularContent: (category: ContentCategory) => Promise<void>;
  loadBestRatedContent: (category: ContentCategory) => Promise<void>;
  loadLatestContent: (category: ContentCategory) => Promise<void>;
  loadHiddenGemsContent: (category: ContentCategory) => Promise<void>;
  loadAllHomeContent: (categories: ContentCategory[]) => Promise<void>;
}

interface UnifiedContentContextValue extends ContentState, ContentActions {
  getContentByCategory: (category: ContentCategory) => ContentItem[];
  getLoadingByCategory: (category: ContentCategory) => boolean;
  getErrorByCategory: (category: ContentCategory) => string | null;
  getSelectedGenreByCategory: (category: ContentCategory) => string;
}

const UnifiedContentContext = createContext<UnifiedContentContextValue | undefined>(undefined);

export const UnifiedContentProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [content, setContent] = useState<Record<ContentCategory, ContentItem[]>>({
    movie: [],
    series: [],
    short: [],
    book: [],
    comic: [],
    novel: [],
    anime: []
  });

  const [loading, setLoading] = useState<Record<ContentCategory, boolean>>({
    movie: false,
    series: false,
    short: false,
    book: false,
    comic: false,
    novel: false,
    anime: false
  });

  const [error, setError] = useState<Record<ContentCategory, string | null>>({
    movie: null,
    series: null,
    short: null,
    book: null,
    comic: null,
    novel: null,
    anime: null
  });

  const [selectedGenre, setSelectedGenreState] = useState<Record<ContentCategory, string>>({
    movie: 'all',
    series: 'all',
    short: 'all',
    book: 'all',
    comic: 'all',
    novel: 'all',
    anime: 'all'
  });

  const [userLikes, setUserLikes] = useState<Record<string, 'like' | 'dislike' | null>>({});
  const [likeCounts, setLikeCounts] = useState<Record<string, { likes: number; dislikes: number }>>({});
  const [liking, setLiking] = useState<Set<string>>(new Set());

  // Home page content state
  const [popularContent, setPopularContent] = useState<Record<ContentCategory, ContentItem[]>>({
    movie: [],
    series: [],
    short: [],
    book: [],
    comic: [],
    novel: [],
    anime: []
  });

  const [bestRatedContent, setBestRatedContent] = useState<Record<ContentCategory, ContentItem[]>>({
    movie: [],
    series: [],
    short: [],
    book: [],
    comic: [],
    novel: [],
    anime: []
  });

  const [latestContent, setLatestContent] = useState<Record<ContentCategory, ContentItem[]>>({
    movie: [],
    series: [],
    short: [],
    book: [],
    comic: [],
    novel: [],
    anime: []
  });

  const [hiddenGemsContent, setHiddenGemsContent] = useState<Record<ContentCategory, ContentItem[]>>({
    movie: [],
    series: [],
    short: [],
    book: [],
    comic: [],
    novel: [],
    anime: []
  });

  const [homeContentLoading, setHomeContentLoading] = useState<Record<ContentCategory, boolean>>({
    movie: false,
    series: false,
    short: false,
    book: false,
    comic: false,
    novel: false,
    anime: false
  });

  const loadContent = useCallback(async (category: ContentCategory, userId?: string) => {
    try {
      setLoading(prev => ({ ...prev, [category]: true }));
      setError(prev => ({ ...prev, [category]: null }));

      let contentData: ContentItem[] = [];
      
      try {
        if (selectedGenre[category] === 'all') {
          contentData = await getContent(category, 50);
        } else {
          contentData = await getContentByGenre(selectedGenre[category], 50, category);
        }
      } catch (firebaseError) {
        console.error(`Firebase error loading ${category}:`, firebaseError);
        contentData = [];
      }

      setContent(prev => ({ ...prev, [category]: contentData }));

      if (userId && contentData.length > 0) {
        try {
          const likesPromises = contentData.map(async (item) => {
            try {
              const [userLike, counts] = await Promise.all([
                getUserLike(item.id, userId),
                getContentLikes(item.id)
              ]);
              return { contentId: item.id, userLike, counts };
            } catch (error) {
              return { contentId: item.id, userLike: null, counts: { likes: 0, dislikes: 0 } };
            }
          });

          const results = await Promise.all(likesPromises);
          
          const newUserLikes: Record<string, 'like' | 'dislike' | null> = {};
          const newLikeCounts: Record<string, { likes: number; dislikes: number }> = {};
          
          results.forEach(({ contentId, userLike, counts }) => {
            newUserLikes[contentId] = userLike;
            newLikeCounts[contentId] = counts;
          });

          setUserLikes(prev => ({ ...prev, ...newUserLikes }));
          setLikeCounts(prev => ({ ...prev, ...newLikeCounts }));
        } catch (likeError) {
          console.error(`Error loading likes for ${category}:`, likeError);
        }
      }

      if (contentData.length === 0) {
        const errorMessages: Record<ContentCategory, string> = {
          movie: 'No movies available. Try uploading some content!',
          series: 'No series available. Try uploading some content!',
          short: 'No shorts available. Try uploading some content!',
          book: 'No books available. Try uploading some content!',
          comic: 'No comics available. Try uploading some content!',
          novel: 'No novels available. Try uploading some content!',
          anime: 'No anime available. Try uploading some content!'
        };
        setError(prev => ({ ...prev, [category]: errorMessages[category] }));
      }
    } catch (err) {
      console.error(`Error loading ${category}:`, err);
      setError(prev => ({ 
        ...prev, 
        [category]: err instanceof Error ? err.message : `Failed to load ${category}` 
      }));
      setContent(prev => ({ ...prev, [category]: [] }));
    } finally {
      setLoading(prev => ({ ...prev, [category]: false }));
    }
  }, [selectedGenre]);

  const setSelectedGenre = useCallback((category: ContentCategory, genre: string) => {
    setSelectedGenreState(prev => ({ ...prev, [category]: genre }));
  }, []);

  const handleLike = useCallback(async (contentId: string, category: ContentCategory, userId: string) => {
    if (liking.has(contentId)) return;

    try {
      setLiking(prev => new Set(prev).add(contentId));
      
      const currentLike = userLikes[contentId];
      const newLikeType = currentLike === 'like' ? 'dislike' : 'like';
      
      const result = await toggleLike(contentId, userId, newLikeType);
      
      setUserLikes(prev => ({
        ...prev,
        [contentId]: result.action === 'removed' ? null : result.newType!
      }));
      
      const updatedCounts = await getContentLikes(contentId);
      setLikeCounts(prev => ({
        ...prev,
        [contentId]: updatedCounts
      }));
    } catch (error) {
      console.error('Error toggling like:', error);
      throw error;
    } finally {
      setLiking(prev => {
        const newSet = new Set(prev);
        newSet.delete(contentId);
        return newSet;
      });
    }
  }, [userLikes, liking]);

  const handleShare = useCallback(async (
    contentId: string, 
    category: ContentCategory, 
    userId?: string,
    shareMethod: 'link' | 'social' | 'embed' = 'link',
    platform?: string
  ) => {
    try {
      const shareUrl = `${window.location.origin}/content/${contentId}`;
      
      // Always copy to clipboard for link sharing
      if (shareMethod === 'link') {
        try {
          await navigator.clipboard.writeText(shareUrl);
        } catch (clipboardError) {
          // Fallback for browsers that don't support clipboard API
          const textArea = document.createElement('textarea');
          textArea.value = shareUrl;
          textArea.style.position = 'fixed';
          textArea.style.opacity = '0';
          document.body.appendChild(textArea);
          textArea.select();
          try {
            document.execCommand('copy');
          } catch (execError) {
            console.error('Failed to copy to clipboard:', execError);
            throw new Error('Failed to copy link to clipboard');
          }
          document.body.removeChild(textArea);
        }
      }
      
      // Record share in database if user is logged in
      if (userId) {
        try {
          await shareContent(userId, contentId, category, shareMethod, platform || 'web');
        } catch (firebaseError) {
          // Silently fail - share still works even if recording fails
          console.error('Error recording share:', firebaseError);
        }
      }
    } catch (error) {
      console.error('Error sharing:', error);
      throw error;
    }
  }, [content]);

  const clearContent = useCallback((category: ContentCategory) => {
    setContent(prev => ({ ...prev, [category]: [] }));
    setError(prev => ({ ...prev, [category]: null }));
    setUserLikes(prev => {
      const newLikes = { ...prev };
      content[category].forEach(item => {
        delete newLikes[item.id];
      });
      return newLikes;
    });
    setLikeCounts(prev => {
      const newCounts = { ...prev };
      content[category].forEach(item => {
        delete newCounts[item.id];
      });
      return newCounts;
    });
  }, [content]);

  // Home page content loading functions
  const loadPopularContent = useCallback(async (category: ContentCategory) => {
    try {
      setHomeContentLoading(prev => ({ ...prev, [category]: true }));
      const contentData = await getMostPopularContent(category, 10);
      setPopularContent(prev => ({ ...prev, [category]: contentData }));
    } catch (error) {
      console.error(`Error loading popular ${category}:`, error);
      setPopularContent(prev => ({ ...prev, [category]: [] }));
    } finally {
      setHomeContentLoading(prev => ({ ...prev, [category]: false }));
    }
  }, []);

  const loadBestRatedContent = useCallback(async (category: ContentCategory) => {
    try {
      setHomeContentLoading(prev => ({ ...prev, [category]: true }));
      const contentData = await getBestRatedContent(category, 10, 10);
      setBestRatedContent(prev => ({ ...prev, [category]: contentData }));
    } catch (error) {
      console.error(`Error loading best rated ${category}:`, error);
      setBestRatedContent(prev => ({ ...prev, [category]: [] }));
    } finally {
      setHomeContentLoading(prev => ({ ...prev, [category]: false }));
    }
  }, []);

  const loadLatestContent = useCallback(async (category: ContentCategory) => {
    try {
      setHomeContentLoading(prev => ({ ...prev, [category]: true }));
      const contentData = await getLatestContent(category, 10);
      setLatestContent(prev => ({ ...prev, [category]: contentData }));
    } catch (error) {
      console.error(`Error loading latest ${category}:`, error);
      setLatestContent(prev => ({ ...prev, [category]: [] }));
    } finally {
      setHomeContentLoading(prev => ({ ...prev, [category]: false }));
    }
  }, []);

  const loadHiddenGemsContent = useCallback(async (category: ContentCategory) => {
    try {
      setHomeContentLoading(prev => ({ ...prev, [category]: true }));
      const contentData = await getHiddenGemsContent(category, 10, 50, 3);
      setHiddenGemsContent(prev => ({ ...prev, [category]: contentData }));
    } catch (error) {
      console.error(`Error loading hidden gems ${category}:`, error);
      setHiddenGemsContent(prev => ({ ...prev, [category]: [] }));
    } finally {
      setHomeContentLoading(prev => ({ ...prev, [category]: false }));
    }
  }, []);

  const loadAllHomeContent = useCallback(async (categories: ContentCategory[]) => {
    const loadPromises: Promise<void>[] = [];
    
    categories.forEach(category => {
      loadPromises.push(
        loadPopularContent(category),
        loadBestRatedContent(category),
        loadLatestContent(category),
        loadHiddenGemsContent(category)
      );
    });

    try {
      await Promise.all(loadPromises);
    } catch (error) {
      console.error('Error loading all home content:', error);
    }
  }, [loadPopularContent, loadBestRatedContent, loadLatestContent, loadHiddenGemsContent]);

  const getContentByCategory = useCallback((category: ContentCategory) => {
    return content[category] || [];
  }, [content]);

  const getLoadingByCategory = useCallback((category: ContentCategory) => {
    return loading[category] || false;
  }, [loading]);

  const getErrorByCategory = useCallback((category: ContentCategory) => {
    return error[category] || null;
  }, [error]);

  const getSelectedGenreByCategory = useCallback((category: ContentCategory) => {
    return selectedGenre[category] || 'all';
  }, [selectedGenre]);

  const value = useMemo(() => ({
    content,
    loading,
    error,
    selectedGenre,
    userLikes,
    likeCounts,
    liking,
    loadContent,
    setSelectedGenre,
    handleLike,
    handleShare,
    clearContent,
    getContentByCategory,
    getLoadingByCategory,
    getErrorByCategory,
    getSelectedGenreByCategory,
    // Home page content
    popularContent,
    bestRatedContent,
    latestContent,
    hiddenGemsContent,
    homeContentLoading,
    loadPopularContent,
    loadBestRatedContent,
    loadLatestContent,
    loadHiddenGemsContent,
    loadAllHomeContent
  }), [
    content,
    loading,
    error,
    selectedGenre,
    userLikes,
    likeCounts,
    liking,
    loadContent,
    setSelectedGenre,
    handleLike,
    handleShare,
    clearContent,
    getContentByCategory,
    getLoadingByCategory,
    getErrorByCategory,
    getSelectedGenreByCategory,
    popularContent,
    bestRatedContent,
    latestContent,
    hiddenGemsContent,
    homeContentLoading,
    loadPopularContent,
    loadBestRatedContent,
    loadLatestContent,
    loadHiddenGemsContent,
    loadAllHomeContent
  ]);

  return (
    <UnifiedContentContext.Provider value={value}>
      {children}
    </UnifiedContentContext.Provider>
  );
};

export const useUnifiedContent = (): UnifiedContentContextValue => {
  const context = useContext(UnifiedContentContext);
  if (!context) {
    throw new Error('useUnifiedContent must be used within UnifiedContentProvider');
  }
  return context;
};

// Backward compatibility hooks - memoized for performance
export const useMovies = () => {
  const ctx = useUnifiedContent();
  const movies = ctx.content.movie;
  const loading = ctx.loading.movie;
  const error = ctx.error.movie;
  const selectedGenre = ctx.selectedGenre.movie;
  
  const loadMovies = useCallback((userId?: string) => ctx.loadContent('movie', userId), [ctx.loadContent]);
  const setSelectedGenre = useCallback((genre: string) => ctx.setSelectedGenre('movie', genre), [ctx.setSelectedGenre]);
  const handleLike = useCallback((movieId: string, userId: string) => ctx.handleLike(movieId, 'movie', userId), [ctx.handleLike]);
  const handleShare = useCallback((movieId: string, userId?: string) => ctx.handleShare(movieId, 'movie', userId), [ctx.handleShare]);
  const clearMovies = useCallback(() => ctx.clearContent('movie'), [ctx.clearContent]);
  
  return useMemo(() => ({
    movies,
    loading,
    error,
    selectedGenre,
    userLikes: ctx.userLikes,
    likeCounts: ctx.likeCounts,
    likingMovies: ctx.liking,
    loadMovies,
    setSelectedGenre,
    handleLike,
    handleShare,
    clearMovies
  }), [
    movies,
    loading,
    error,
    selectedGenre,
    ctx.userLikes,
    ctx.likeCounts,
    ctx.liking,
    loadMovies,
    setSelectedGenre,
    handleLike,
    handleShare,
    clearMovies
  ]);
};

export const useSeries = () => {
  const ctx = useUnifiedContent();
  const series = ctx.content.series;
  const loading = ctx.loading.series;
  const error = ctx.error.series;
  const selectedGenre = ctx.selectedGenre.series;
  
  const loadSeries = useCallback((userId?: string) => ctx.loadContent('series', userId), [ctx.loadContent]);
  const setSelectedGenre = useCallback((genre: string) => ctx.setSelectedGenre('series', genre), [ctx.setSelectedGenre]);
  const handleLike = useCallback((seriesId: string, userId: string) => ctx.handleLike(seriesId, 'series', userId), [ctx.handleLike]);
  const handleShare = useCallback(async (seriesId: string, contentType: string, shareMethod: 'link' | 'social' | 'embed', platform?: string, userId?: string) => {
    await ctx.handleShare(seriesId, 'series', userId, shareMethod, platform);
  }, [ctx.handleShare]);
  
  return useMemo(() => ({
    series,
    loading,
    error,
    selectedGenre,
    userLikes: ctx.userLikes,
    likeCounts: ctx.likeCounts,
    likingSeries: ctx.liking,
    loadSeries,
    setSelectedGenre,
    handleLike,
    handleShare
  }), [
    series,
    loading,
    error,
    selectedGenre,
    ctx.userLikes,
    ctx.likeCounts,
    ctx.liking,
    loadSeries,
    setSelectedGenre,
    handleLike,
    handleShare
  ]);
};

export const useShorts = () => {
  const ctx = useUnifiedContent();
  const shorts = ctx.content.short;
  const loading = ctx.loading.short;
  const error = ctx.error.short;
  const selectedGenre = ctx.selectedGenre.short;
  
  const loadShorts = useCallback((userId?: string) => ctx.loadContent('short', userId), [ctx.loadContent]);
  const setSelectedGenre = useCallback((genre: string) => ctx.setSelectedGenre('short', genre), [ctx.setSelectedGenre]);
  const handleLike = useCallback((shortId: string, userId: string) => ctx.handleLike(shortId, 'short', userId), [ctx.handleLike]);
  const handleShare = useCallback((shortId: string, userId?: string) => ctx.handleShare(shortId, 'short', userId), [ctx.handleShare]);
  const clearShorts = useCallback(() => ctx.clearContent('short'), [ctx.clearContent]);
  
  return useMemo(() => ({
    shorts,
    loading,
    error,
    selectedGenre,
    userLikes: ctx.userLikes,
    likeCounts: ctx.likeCounts,
    likingShorts: ctx.liking,
    loadShorts,
    setSelectedGenre,
    handleLike,
    handleShare,
    clearShorts
  }), [
    shorts,
    loading,
    error,
    selectedGenre,
    ctx.userLikes,
    ctx.likeCounts,
    ctx.liking,
    loadShorts,
    setSelectedGenre,
    handleLike,
    handleShare,
    clearShorts
  ]);
};

export const useBooks = () => {
  const ctx = useUnifiedContent();
  const books = ctx.content.book;
  const loading = ctx.loading.book;
  const error = ctx.error.book;
  const selectedGenre = ctx.selectedGenre.book;
  
  const loadBooks = useCallback((userId?: string) => ctx.loadContent('book', userId), [ctx.loadContent]);
  const setSelectedGenre = useCallback((genre: string) => ctx.setSelectedGenre('book', genre), [ctx.setSelectedGenre]);
  const handleLike = useCallback((bookId: string, userId: string) => ctx.handleLike(bookId, 'book', userId), [ctx.handleLike]);
  const handleShare = useCallback((bookId: string, userId?: string) => ctx.handleShare(bookId, 'book', userId), [ctx.handleShare]);
  const clearBooks = useCallback(() => ctx.clearContent('book'), [ctx.clearContent]);
  
  return useMemo(() => ({
    books,
    loading,
    error,
    selectedGenre,
    userLikes: ctx.userLikes,
    likeCounts: ctx.likeCounts,
    likingBooks: ctx.liking,
    loadBooks,
    setSelectedGenre,
    handleLike,
    handleShare,
    clearBooks
  }), [
    books,
    loading,
    error,
    selectedGenre,
    ctx.userLikes,
    ctx.likeCounts,
    ctx.liking,
    loadBooks,
    setSelectedGenre,
    handleLike,
    handleShare,
    clearBooks
  ]);
};

export const useComics = () => {
  const ctx = useUnifiedContent();
  const comics = ctx.content.comic;
  const loading = ctx.loading.comic;
  const error = ctx.error.comic;
  const selectedGenre = ctx.selectedGenre.comic;
  
  const loadComics = useCallback((userId?: string) => ctx.loadContent('comic', userId), [ctx.loadContent]);
  const setSelectedGenre = useCallback((genre: string) => ctx.setSelectedGenre('comic', genre), [ctx.setSelectedGenre]);
  const handleLike = useCallback((comicId: string, userId: string) => ctx.handleLike(comicId, 'comic', userId), [ctx.handleLike]);
  const handleShare = useCallback((comicId: string, userId?: string) => ctx.handleShare(comicId, 'comic', userId), [ctx.handleShare]);
  const clearComics = useCallback(() => ctx.clearContent('comic'), [ctx.clearContent]);
  
  return useMemo(() => ({
    comics,
    loading,
    error,
    selectedGenre,
    userLikes: ctx.userLikes,
    likeCounts: ctx.likeCounts,
    likingComics: ctx.liking,
    loadComics,
    setSelectedGenre,
    handleLike,
    handleShare,
    clearComics
  }), [
    comics,
    loading,
    error,
    selectedGenre,
    ctx.userLikes,
    ctx.likeCounts,
    ctx.liking,
    loadComics,
    setSelectedGenre,
    handleLike,
    handleShare,
    clearComics
  ]);
};

export const useNovels = () => {
  const ctx = useUnifiedContent();
  const novels = ctx.content.novel;
  const loading = ctx.loading.novel;
  const error = ctx.error.novel;
  const selectedGenre = ctx.selectedGenre.novel;
  
  const loadNovels = useCallback((userId?: string) => ctx.loadContent('novel', userId), [ctx.loadContent]);
  const setSelectedGenre = useCallback((genre: string) => ctx.setSelectedGenre('novel', genre), [ctx.setSelectedGenre]);
  const handleLike = useCallback((novelId: string, userId: string) => ctx.handleLike(novelId, 'novel', userId), [ctx.handleLike]);
  const handleShare = useCallback(async (novelId: string, contentType: string, shareMethod: 'link' | 'social' | 'embed', platform?: string, userId?: string) => {
    await ctx.handleShare(novelId, 'novel', userId, shareMethod, platform);
  }, [ctx.handleShare]);
  
  return useMemo(() => ({
    novels,
    loading,
    error,
    selectedGenre,
    userLikes: ctx.userLikes,
    likeCounts: ctx.likeCounts,
    likingNovels: ctx.liking,
    loadNovels,
    setSelectedGenre,
    handleLike,
    handleShare
  }), [
    novels,
    loading,
    error,
    selectedGenre,
    ctx.userLikes,
    ctx.likeCounts,
    ctx.liking,
    loadNovels,
    setSelectedGenre,
    handleLike,
    handleShare
  ]);
};

export const useAnime = () => {
  const ctx = useUnifiedContent();
  const anime = ctx.content.anime;
  const loading = ctx.loading.anime;
  const error = ctx.error.anime;
  const selectedGenre = ctx.selectedGenre.anime;
  
  const loadAnime = useCallback((userId?: string) => ctx.loadContent('anime', userId), [ctx.loadContent]);
  const setSelectedGenre = useCallback((genre: string) => ctx.setSelectedGenre('anime', genre), [ctx.setSelectedGenre]);
  const handleLike = useCallback((animeId: string, userId: string) => ctx.handleLike(animeId, 'anime', userId), [ctx.handleLike]);
  const handleShare = useCallback((animeId: string, userId?: string) => ctx.handleShare(animeId, 'anime', userId), [ctx.handleShare]);
  const clearAnime = useCallback(() => ctx.clearContent('anime'), [ctx.clearContent]);
  
  return useMemo(() => ({
    anime,
    loading,
    error,
    selectedGenre,
    userLikes: ctx.userLikes,
    likeCounts: ctx.likeCounts,
    likingAnime: ctx.liking,
    loadAnime,
    setSelectedGenre,
    handleLike,
    handleShare,
    clearAnime
  }), [
    anime,
    loading,
    error,
    selectedGenre,
    ctx.userLikes,
    ctx.likeCounts,
    ctx.liking,
    loadAnime,
    setSelectedGenre,
    handleLike,
    handleShare,
    clearAnime
  ]);
};

// Categories constant - defined outside to prevent recreation
const HOME_CATEGORIES: ContentCategory[] = ['movie', 'series', 'short', 'book', 'comic'];

// Optimized hook for home page - loads all content types for all categories in parallel
export const useHomeContent = (userId?: string) => {
  const ctx = useUnifiedContent();
  const [isLoading, setIsLoading] = React.useState(false);
  const hasLoadedRef = React.useRef(false);
  
  const loadAllContent = useCallback(async () => {
    if (hasLoadedRef.current) return; // Prevent multiple loads
    hasLoadedRef.current = true;
    setIsLoading(true);
    try {
      await ctx.loadAllHomeContent(HOME_CATEGORIES);
    } catch (error) {
      console.error('Error loading home content:', error);
      hasLoadedRef.current = false; // Reset on error so user can retry
    } finally {
      setIsLoading(false);
    }
  }, [ctx.loadAllHomeContent]);

  // Check if any category is loading
  const anyCategoryLoading = HOME_CATEGORIES.some(cat => ctx.homeContentLoading[cat]);

  return useMemo(() => ({
    // Popular content
    popularMovies: ctx.popularContent.movie,
    popularSeries: ctx.popularContent.series,
    popularShorts: ctx.popularContent.short,
    popularBooks: ctx.popularContent.book,
    popularComics: ctx.popularContent.comic,
    // Best rated content
    bestRatedMovies: ctx.bestRatedContent.movie,
    bestRatedSeries: ctx.bestRatedContent.series,
    bestRatedShorts: ctx.bestRatedContent.short,
    bestRatedBooks: ctx.bestRatedContent.book,
    bestRatedComics: ctx.bestRatedContent.comic,
    // Latest content
    latestMovies: ctx.latestContent.movie,
    latestSeries: ctx.latestContent.series,
    latestShorts: ctx.latestContent.short,
    latestBooks: ctx.latestContent.book,
    latestComics: ctx.latestContent.comic,
    // Hidden gems content
    hiddenGemsMovies: ctx.hiddenGemsContent.movie,
    hiddenGemsSeries: ctx.hiddenGemsContent.series,
    hiddenGemsShorts: ctx.hiddenGemsContent.short,
    hiddenGemsBooks: ctx.hiddenGemsContent.book,
    hiddenGemsComics: ctx.hiddenGemsContent.comic,
    // Loading state
    loading: isLoading || anyCategoryLoading,
    loadAllContent
  }), [
    ctx.popularContent,
    ctx.bestRatedContent,
    ctx.latestContent,
    ctx.hiddenGemsContent,
    ctx.homeContentLoading,
    isLoading,
    anyCategoryLoading,
    loadAllContent
  ]);
};

